import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { MCard, MSpinner } from "components/Shared";
import { userHasRight } from "selectors/rights";
import { CUSTOMER_ACCOUNT_EDIT } from "typeConstants/rights";
import {
  DATA_TYPE_DATE,
  DATA_TYPE_PHONE_NUMBER,
  DATA_TYPE_ZIP,
  DATA_TYPE_DROPDOWN2,
} from "typeConstants/dataTypes";
import { updateCustomer } from "requests/customer";
import { getCustomerDetails, getMajorCompanies } from "actions/customer";
import * as yup from "yup";
import { getStates } from "actions/region";
import { mapCustomerDetailsToCustomerUpdate } from "components/Customer/Profile/CustomerEmailCaseSettings.tsx";

const CustomerDetails = ({
  customerDetails,
  cellWidth,
  getCustomerDetails,
  canEditAccountDetails,
  states,
  getStates,
  majorCompanies,
  getMajorCompanies,
}) => {
  const mounted = useRef(true);
  const cardControlMethods = useRef();

  useEffect(() => {
    if (!states?.length) getStates();
    if (!majorCompanies?.length) getMajorCompanies();
    return () => mounted.current = false;
  }, []);

  const customerEditFinished = (fieldsChanged) => {
    if (fieldsChanged.length) {
      let postObj = { ...mapCustomerDetailsToCustomerUpdate(customerDetails), ...cardControlMethods.current.getEditedDataObject(true) };
      postObj.customerNumber = customerDetails.number;
      updateCustomerWrapped(postObj);
    }
  };

  const updateCustomerWrapped = (payload) => updateCustomer(customerDetails.number, payload, () => getCustomerDetails(customerDetails.number), mounted);

  let customerDetailFields = [];

  const makeYupValidation = (requiredMessage = null, regex = null, regexFailedMessage = null) => {
    let yerp = yup.string().nullable();
    
    if (requiredMessage) yerp = yerp.required(requiredMessage);
    if (regex) yerp = yerp.matches(regex, { excludeEmptyString: true, message: regexFailedMessage });

    return yerp;
  };

  if (customerDetails && states?.length && majorCompanies?.length) {
    customerDetailFields = [
      {
        label: "Account Number",
        value: customerDetails.number,
        editable: false,
      },
      {
        key: "name",
        label: "Account Name",
        value: customerDetails.name,
        editable: canEditAccountDetails,
        validation: makeYupValidation("Account Name is required"),
      },
      {
        key: "displayName",
        label: "Display Name",
        value: customerDetails.displayName,
        editable: canEditAccountDetails,
        validation: makeYupValidation("Display Name is required"),
      },
      {
        key: "companyId",
        label: "Company",
        value: { label: customerDetails.majorCompany.companyName, value: customerDetails.majorCompany.id },
        editable: canEditAccountDetails,
        validation: makeYupValidation("Company is required"),
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: { options: majorCompanies.map(v => ({ label: v.companyName, value: v.id })), nullable: false }
      },
      {
        key: "address1",
        label: "Address",
        value: customerDetails.mailingAddress?.address1,
        editable: canEditAccountDetails,
        validation: makeYupValidation("Address is required"),
      },
      {
        key: "address2",
        label: "Address 2",
        value: customerDetails.mailingAddress?.address2,
        editable: canEditAccountDetails,
      },
      {
        key: "city",
        label: "City",
        value: customerDetails.mailingAddress?.city,
        editable: canEditAccountDetails,
        validation: makeYupValidation("City is required"),
      },
      {
        key: "stateAbbreviation",
        label: "State",
        value: { label: states.find(s => s.abbreviation === customerDetails.mailingAddress?.state)?.name, value: customerDetails.mailingAddress?.state },
        editable: canEditAccountDetails,
        validation: makeYupValidation("State is required"),
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: { options: states?.map(s => ({ label: s.name, value: s.abbreviation })), nullable: false },
      },
      {
        key: "zip",
        label: "Zip",
        value: customerDetails.mailingAddress?.zip,
        editable: canEditAccountDetails,
        dataType: DATA_TYPE_ZIP,
        validation: makeYupValidation("Zip is required", /^[0-9]{5,9}$/, "Zip code is invalid"), // 5 digits only numbers
      },
      {
        key: "phoneNumber1",
        label: "Phone 1",
        value: customerDetails.phoneNumber1,
        editable: canEditAccountDetails,
        dataType: DATA_TYPE_PHONE_NUMBER,
        validation: makeYupValidation(null, /[0-9]{10}/, "Phone number is invalid"),
      },
      {
        key: "phoneNumber2",
        label: "Phone 2",
        value: customerDetails.phoneNumber2,
        editable: canEditAccountDetails,
        dataType: DATA_TYPE_PHONE_NUMBER,
        validation: makeYupValidation(null, /[0-9]{10}/, "Phone number is invalid"),
      },
      {
        key: "inactiveDate",
        label: "Inactive Date",
        value: customerDetails.inactiveDate,
        editable: canEditAccountDetails,
        dataType: DATA_TYPE_DATE,
      },
    ];
  }

  if (!states?.length || !majorCompanies?.length) {
    return <MSpinner />;
  } else {
    return (
      <MCard
        isFetching={!customerDetailFields.length}
        title="Account Details"
        cellWidth={cellWidth}
        fields={customerDetailFields}
        isEditable={customerDetailFields.some(f => f.editable)}
        onEditFinished={customerEditFinished}
        getControlMethods={cm => cardControlMethods.current = cm}
      />
    );
  }
};

CustomerDetails.propTypes = {
  customerDetails: PropTypes.object,
  cellWidth: PropTypes.number,
  getCustomerDetails: PropTypes.func,
  states: PropTypes.array,
  getStates: PropTypes.func.isRequired,
  getMajorCompanies: PropTypes.func.isRequired,
  majorCompanies: PropTypes.array,
  canEditAccountDetails: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  customerDetails: state.customer.customerDetails,
  canEditAccountDetails: userHasRight(CUSTOMER_ACCOUNT_EDIT)(state),
  states: state.region.states,
  majorCompanies: state.customer.majorCompanies,
});

const mapDispatchToProps = dispatch => ({
  getCustomerDetails: (customerNumber) => dispatch(getCustomerDetails(customerNumber)),
  getStates() {
    dispatch(getStates());
  },
  getMajorCompanies() {
    dispatch(getMajorCompanies());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomerDetails);
